import { call, put, select } from 'redux-saga/effects';
import carDetailsActions from 'redux/actions/vehicleDetails';
import {
  selectTenantId,
  selectMoveVersion,
  selectPartnerReference,
  partnerSelector,
  shouldSkipGetPlateSelector,
} from 'redux/selectors/config';
import getVehicleDetails from 'api/vehicleDetails';
import { getSynergySessionToken } from 'utils/sagas-utils';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import type { ApiResponse } from 'ts/interfaces/api-response.interface';

import { addGtmDataLayer } from 'redux/actions/gtmActions';
import { ga4Event } from 'analytics';
import { getRoute } from 'analytics/config/utils';

const { startLoad, stopLoad } = applicationSpinnerActions;
const { getPlateSuccess, getPlateFail } = carDetailsActions;

const FORM_NAME = 'request assistance';

export function* getVehicleDetailsBaseSaga(plateNumber: string) {
  const shouldSkipGetPlate: boolean = yield select(shouldSkipGetPlateSelector);
  const partnerReference: number = yield select(selectPartnerReference);
  const partnerName: string = yield select(partnerSelector);
  const tenantId: string = yield select(selectTenantId);
  const moveVersion: string = yield select(selectMoveVersion);
  const sessionToken: string = yield getSynergySessionToken();

  let carDetailsData = {
    registration_number: plateNumber,
  };

  const response: ApiResponse<any> = yield call(getVehicleDetails, {
    registration_number: plateNumber,
    partnerReference,
    partnerName,
    tenantId,
    moveVersion,
    sessionToken,
  });

  if (!response.ok && !shouldSkipGetPlate) {
    throw new Error(`Car details api error: ${response?.result}`);
  }

  yield call(ga4Event, {
    eventaction: !response.ok ? 'form_error' : 'form_success',
    payload: {
      form_name: FORM_NAME,
    },
  });

  if (response.result && response.result?.length > 0) {
    const [data] = response.result;
    carDetailsData = {
      ...carDetailsData,
      ...data,
      type: data?.vehicle_details,
    };
  }
  return carDetailsData;
}

export function* getVehicleDetailsSaga(
  action: ReturnType<typeof carDetailsActions.getPlate>,
) {
  const { registration_number, shouldUseGlobalLoader } = action.payload;
  if (shouldUseGlobalLoader) {
    yield put(startLoad({}));
  }
  try {
    yield call(ga4Event, {
      eventaction: 'case_creation_start',
      payload: {
        origin: getRoute(window.location.pathname).page_name,
      },
    });

    // @ts-ignore
    const vehicleDetailsData = yield call(
      getVehicleDetailsBaseSaga,
      registration_number,
    );

    yield put(getPlateSuccess(vehicleDetailsData));

    yield put(
      addGtmDataLayer({
        vehicle_type: vehicleDetailsData?.vehicle_type || 'car',
      }),
    );
  } catch (error) {
    yield call(ga4Event, {
      eventaction: 'form_error',
      payload: {
        form_name: FORM_NAME,
      },
    });
    yield put(getPlateFail(error as any));
  } finally {
    if (shouldUseGlobalLoader) {
      yield put(stopLoad({}));
    }
  }
}
